import { getSupabaseClient } from './supabase.client.js';

/**
 * Repositorio de `adelantos` (S4, design.md AD8/AD9/AD11) para el panel de
 * staff. SOLO LECTURA desde el navegador: la tabla es append-only, con
 * únicamente una política de SELECT admin (`adelantos_admin_read`,
 * migración 28) y SIN política de INSERT/UPDATE/DELETE. Los únicos
 * escritores son las RPCs `crear_adelanto`/`anular_adelanto` (SECURITY
 * DEFINER, `revoke ... from public, anon, authenticated` + `grant to
 * service_role`) — este repositorio, que corre con la anon key del
 * navegador, no tiene grant para escribir la tabla directo; la escritura
 * real vive en `api/payroll-movimientos.js` (service_role).
 *
 * Columnas SIEMPRE explícitas (nunca `select('*')`), mismo patrón que
 * `liquidaciones.repository.js`/`feriados.repository.js`.
 */

const ADELANTO_COLUMNS =
    'id, empleado_id, fecha, monto, nota, creado_por, creado_at, anulado_at, anulado_por, anulado_motivo';

/**
 * Re-lanza un error de Supabase preservando el código de Postgres (mismo
 * patrón que el resto de los repositorios de este panel).
 * @param {{ message: string, code?: string }} error
 * @returns {never}
 */
function rethrow(error) {
    const err = new Error(error.message);
    if (error.code) err.code = error.code;
    throw err;
}

/**
 * Adelantos (activos y anulados), fecha descendente. `empleadoIds`/`desde`/
 * `hasta` (si se pasan) filtran por `empleado_id`/`fecha` — los tres son
 * opcionales, mismo criterio que `listLiquidaciones`. Anulados incluidos a
 * propósito (no se filtra `anulado_at is null` acá): el drawer necesita
 * mostrar la lista activa Y la anulada (tasks.md S4 "active/voided list");
 * el caller decide cómo particionarlos.
 *
 * Si Supabase no está configurado devuelve `[]` (misma degradación que
 * `listFichadas`/`listEmpleados`/`listLiquidaciones`).
 * @param {{ empleadoIds?: string[], desde?: string, hasta?: string }} [params]
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error
 */
export async function listAdelantos({ empleadoIds, desde, hasta } = {}) {
    const client = getSupabaseClient();
    if (!client) return [];

    let query = client.from('adelantos').select(ADELANTO_COLUMNS).order('fecha', { ascending: false });
    if (Array.isArray(empleadoIds) && empleadoIds.length > 0) query = query.in('empleado_id', empleadoIds);
    if (desde) query = query.gte('fecha', desde);
    if (hasta) query = query.lte('fecha', hasta);

    const { data, error } = await query;
    if (error) rethrow(error);
    return data ?? [];
}
